import _ from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import { computedFn } from "mobx-utils";
import { StudentWithAttendanceDTO } from "src/dto";

import { studentDomain } from "..";
import AttendanceService from "./Attendance.service";

// 같은 학생을 처리한 직후 이 시간(ms) 안에 들어온 반대 토글은
// 연타/버퍼링된 터치로 보고 무시한다. (등원 직후 자동 하원 방지)
const TOGGLE_COOLDOWN_MS = 2000;

export class Attendance {
  // 학생별 마지막 출결 처리 시각. UI 이벤트 종류와 무관하게 멱등성을 보장한다.
  private lastProcessedAt = new Map<StudentWithAttendanceDTO["id"], number>();
  // 출결 처리가 일어날 때마다 증가. 주기 갱신 응답이 처리 직전 상태를
  // 담고 있는 경우(응답이 늦게 도착)를 걸러내는 데 쓴다.
  private mutationVersion = 0;

  constructor(private attendanceService: AttendanceService) {
    makeAutoObservable(this);
  }

  get isViewList(): "HIDDEN" | "VIEW" {
    return this.attendanceService.isViewStudentList;
  }

  initIsViewStudentList() {
    this.attendanceService.initIsViewStudentList();
  }

  getAttendanceTarget = computedFn((codeStr: string) => {
    if (codeStr.length !== 4) return undefined;
    const target = studentDomain.studentList.find(
      (student) => student.attendanceCode === codeStr
    );
    return target;
  });

  getAttendanceText = computedFn((codeStr: string) => {
    if (codeStr.length !== 4) return "출석";

    const target = this.getAttendanceTarget(codeStr);

    if (target) {
      return target.attendanceStatus === "QUIT" ? "출석" : "퇴실";
    }

    return "출석";
  });

  async fetchStudentAttendanceList() {
    const version = this.mutationVersion;
    const studentList = await studentDomain.fetchStudents();
    const studentWithAttendanceList =
      await this.attendanceService.fetchStudentAttendanceList(studentList);
    // 조회하는 사이에 출결 처리가 끼어들었으면 낡은 응답으로 덮어쓰지 않는다.
    if (version !== this.mutationVersion) return;
    runInAction(() => {
      studentDomain.studentList = _.orderBy(
        studentWithAttendanceList,
        ["name"],
        ["asc"]
      );
    });
  }

  async fetchStudentAttendance(codeStr: string) {
    const target = this.getAttendanceTarget(codeStr);
    if (!target) return;

    // 직전 처리 후 쿨다운 내 재호출(연타/버퍼링된 터치)이면 토글하지 않고 무시한다.
    const now = Date.now();
    const lastAt = this.lastProcessedAt.get(target.id);
    if (lastAt && now - lastAt < TOGGLE_COOLDOWN_MS) return;
    this.lastProcessedAt.set(target.id, now);
    this.mutationVersion += 1;

    // 처리 시작/종료 양쪽에서 버전을 올려, 처리 구간과 겹친 조회는
    // 성공/실패와 무관하게 모두 무효가 되게 한다.
    try {
      target.attendanceStatus === "QUIT"
        ? await this.attendanceService.putStudentEnter(target)
        : await this.attendanceService.putStudentQuit(target);
    } finally {
      this.mutationVersion += 1;
    }

    const removedList = studentDomain.studentList.filter(
      (student) => student.id !== target.id
    );
    const targetRaisedList = [target, ...removedList];
    runInAction(() => {
      studentDomain.studentList = targetRaisedList;
    });
    return target.attendanceStatus;
  }

  toggleViewStudentList() {
    runInAction(() => {
      this.attendanceService.toggleViewStudentList();
    });
  }
}

const attendanceDomain = new Attendance(new AttendanceService());
export default attendanceDomain;
